import { Avatar } from '@mui/material';
import { useEffect, useState } from 'react';

// Backend now exposes an optional `iconUrl` on tokens and companies. Treat it
// as possibly falsy (string | null | absent) — any falsy value means "no icon"
// and we render an initials fallback. See docs/frontend-icons.md.
export const hasIcon = (iconUrl) =>
  typeof iconUrl === 'string' && iconUrl.length > 0;

// Deterministic colour from a label so fallback avatars stay stable per asset.
const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < (str?.length || 0); i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
};

const deriveInitials = (text) =>
  (text || '?').toString().slice(0, 3).toUpperCase() || '?';

/**
 * Renders an asset (token/company) icon defensively:
 * - fixed-size box + `object-fit: contain` (icon dimensions vary by source)
 * - lazy loading (avoids request bursts in long lists)
 * - `onError` fallback to initials on a coloured background (URLs are
 *   arbitrary third-party links that may 404 or be blocked)
 */
export default function AssetIcon({
  iconUrl,
  label = '',
  initials,
  size = 32,
  variant = 'circular',
  fontSize,
}) {
  const [errored, setErrored] = useState(false);

  // Reset the error state if the URL changes on a reused instance.
  useEffect(() => {
    setErrored(false);
  }, [iconUrl]);

  const showImage = hasIcon(iconUrl) && !errored;
  const fallbackText = deriveInitials(initials ?? label);

  return (
    <Avatar
      src={showImage ? iconUrl : undefined}
      alt={label || undefined}
      variant={variant}
      imgProps={{ loading: 'lazy', onError: () => setErrored(true) }}
      sx={{
        width: size,
        height: size,
        fontSize: fontSize ?? Math.max(10, Math.round(size * 0.34)),
        fontWeight: 700,
        flexShrink: 0,
        // White backing only while showing a (possibly transparent) logo;
        // fall back to a coloured chip once we render initials.
        bgcolor: showImage ? '#ffffff' : stringToColor(fallbackText),
        border: showImage ? '1px solid #e0e0e0' : 'none',
        '& .MuiAvatar-img': { objectFit: 'contain', p: '2px' },
      }}
    >
      {fallbackText}
    </Avatar>
  );
}
